import { useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Plus,
  ShoppingBag,
  X,
} from 'lucide-react'
import { getTileState } from '../constants'
import type { TileProduct, TileState } from '../constants'
import type { Product } from '@/api/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useProducts } from '@/features/products/hooks/use-products'

/** Badge shown per row for a state that keeps the SKU off the checkout step — `ready` needs none. */
const ADDON_STATE_BADGES: Partial<
  Record<TileState, { variant: 'destructive' | 'secondary'; labelKey: string }>
> = {
  'no-price': { variant: 'destructive', labelKey: 'tileState.noPrice' },
  deactivated: { variant: 'destructive', labelKey: 'tileState.inactive' },
  'not-assigned': { variant: 'destructive', labelKey: 'tileState.notAssigned' },
  unresolved: { variant: 'destructive', labelKey: 'tileState.unresolved' },
  'open-price': { variant: 'secondary', labelKey: 'tileState.openPrice' },
}

function AddonPicker({
  restaurantId,
  excludeSkus,
  onSelect,
  disabled,
}: {
  restaurantId: number
  excludeSkus: Array<string>
  onSelect: (product: { sku: string; name: string }) => void
  disabled: boolean
}) {
  const { t: tPosLayout } = useTranslation('pos-layout')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { data } = useProducts({
    restaurantId,
    status: 'active',
    search: search || undefined,
    pageSize: 20,
    // A merged duplicate (CHG-2026-09-duplicate-product-merge) must not be offered as a checkout
    // add-on — only its primary can.
    excludeMerged: true,
  })

  // The list endpoint has no "not open-price" filter, so exclude those — and already-added SKUs —
  // client-side.
  const candidates = (data?.items ?? []).filter(
    (product) => !product.openPrice && !excludeSkus.includes(product.sku),
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Plus className="mr-2 h-4 w-4" />
          {tPosLayout('checkoutAddons.addButton')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={tPosLayout('checkoutAddons.searchPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{tPosLayout('checkoutAddons.empty')}</CommandEmpty>
            <CommandGroup>
              {candidates.map((product) => (
                <CommandItem
                  key={product.sku}
                  value={product.sku}
                  onSelect={() => {
                    onSelect({ sku: product.sku, name: product.name })
                    setOpen(false)
                    setSearch('')
                  }}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{product.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {product.sku}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CheckoutAddonsCardProps {
  restaurantId: number
  addonSkus: Array<string>
  tileProductFor: (sku: string) => TileProduct
  ineligibleSkus: Array<string>
  onAdd: (sku: string) => void
  onRemove: (sku: string) => void
  onMove: (index: number, direction: -1 | 1) => void
  canEdit: boolean
}

export function CheckoutAddonsCard({
  restaurantId,
  addonSkus,
  tileProductFor,
  ineligibleSkus,
  onAdd,
  onRemove,
  onMove,
  canEdit,
}: CheckoutAddonsCardProps) {
  const { t: tPosLayout } = useTranslation('pos-layout')

  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-lg font-semibold">
            {tPosLayout('checkoutAddons.title')}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {tPosLayout('checkoutAddons.description')}
        </p>

        {ineligibleSkus.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {tPosLayout('checkoutAddons.saveBlocked', {
              count: ineligibleSkus.length,
            })}
          </div>
        )}

        <div className="space-y-2">
          {addonSkus.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {tPosLayout('checkoutAddons.emptyList')}
            </p>
          ) : (
            addonSkus.map((sku, index) => {
              const tileProduct = tileProductFor(sku)
              const state = getTileState(tileProduct)
              const product: Product | undefined =
                tileProduct.status === 'found' ? tileProduct.product : undefined
              const badge = ADDON_STATE_BADGES[state]
              const price =
                state === 'ready' && product?.currentPrice !== undefined
                  ? `€${(product.currentPrice / 100).toFixed(2)}`
                  : tPosLayout('values.priceEmpty')

              return (
                <div
                  key={sku}
                  className="flex items-center gap-3 rounded-lg border px-4 py-3"
                >
                  <div className="flex flex-col shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      aria-label={tPosLayout('checkoutAddons.moveUpAria')}
                      disabled={!canEdit || index === 0}
                      onClick={() => onMove(index, -1)}
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5"
                      aria-label={tPosLayout('checkoutAddons.moveDownAria')}
                      disabled={!canEdit || index === addonSkus.length - 1}
                      onClick={() => onMove(index, 1)}
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {product?.name || sku}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {sku}
                    </div>
                  </div>
                  {badge && (
                    <Badge variant={badge.variant}>
                      {tPosLayout(badge.labelKey)}
                    </Badge>
                  )}
                  <div className="text-sm font-semibold tabular-nums shrink-0">
                    {price}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={tPosLayout('checkoutAddons.removeAria')}
                    disabled={!canEdit}
                    onClick={() => onRemove(sku)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )
            })
          )}
        </div>

        <AddonPicker
          restaurantId={restaurantId}
          excludeSkus={addonSkus}
          onSelect={(product) => onAdd(product.sku)}
          disabled={!canEdit}
        />
      </CardContent>
    </Card>
  )
}
